import { useNavigate, NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
const { Users, FolderTree, LayoutList, LogOut, ArrowLeft, Activity, CalendarDays } = Icons;

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import OrganizationRail, { useShowOrgRail, CurrentOrgCaption } from '../../components/ui/OrganizationRail';




// O'zbek digraf harflarini hisobga olib birinchi harfni oladi: Sh, Ch, G', O', Ng
function getInitial(name) {
    if (!name) return '';
    const digraphs = ["Sh", "Ch", "G'", "O'", "Ng", "sh", "ch", "g'", "o'", "ng"];
    for (const d of digraphs) {
        if (name.startsWith(d)) return d.charAt(0).toUpperCase() + d.charAt(1);
    }
    return name.charAt(0).toUpperCase();
}

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const su = user?.is_superadmin;
    const canManageOrgs = su || !!user?.can_manage_organizations;
    const isFullAdmin = su || user?.role === 'admin';
    const showOrgRail = useShowOrgRail();

    // Har bir bo'lim uchun alohida ruxsat tekshiruvi
    const canSeeUsers = isFullAdmin || user?.can_create_users || user?.can_delete_users;
    const canSeeCategories = isFullAdmin ||
        user?.can_create_categories || user?.can_edit_categories || user?.can_delete_categories ||
        user?.can_edit_category_order;
    const canSeeSubcategories = isFullAdmin ||
        user?.can_create_subcategories || user?.can_edit_subcategories || user?.can_delete_subcategories ||
        user?.can_edit_subcategory_order;
    // Gantt — isFullAdmin qo'shilmaydi: faqat aniq gantt ruxsatlari bo'lsa ko'rinadi
    const canSeeGantt = su ||
        user?.can_manage_gantt || user?.can_view_gantt || user?.can_create_gantt ||
        user?.can_edit_gantt || user?.can_delete_gantt || user?.role === 'gantt_user';
    // Docs — isFullAdmin qo'shilmaydi: faqat aniq docs ruxsatlari bo'lsa ko'rinadi
    const canSeeDocs = su ||
        user?.can_view_documents || user?.can_create_documents || user?.can_edit_documents ||
        user?.can_delete_documents || user?.can_manage_doc_approvers || user?.can_import_doc_files ||
        user?.can_unlock_documents || user?.role === 'doc_user';
    const canSeeHistory = isFullAdmin;

    // Foydalanuvchi ruxsatiga qarab ko'rsatiladigan menu elementlari
    const mainNavItems = [
        canSeeUsers       && { path: '/admin/users',         label: t('nav.users'),                        icon: Users },
        canManageOrgs     && { path: '/admin/organizations', label: t('nav.organizations', "Bo'limlar"),     icon: Icons.Building2 },
        canSeeCategories  && { path: '/admin/categories',    label: t('nav.categories'),                   icon: FolderTree },
        canSeeSubcategories && { path: '/admin/subcategories', label: t('nav.subcategories'),              icon: LayoutList },
        canSeeGantt       && { path: '/admin/gantt',         label: t('panel.gantt_chart', 'Gantt Chart'), icon: CalendarDays },
        canSeeDocs        && { path: '/admin/docs',          label: t('panel.documents', 'Hujjatlar'),     icon: Icons.FileText },
    ].filter(Boolean);

    const bottomNavItems = [
        canSeeHistory && { path: '/admin/history', label: t('nav.history'), icon: Activity },
    ].filter(Boolean);

    function handleLogout() {
        logout();
        navigate('/login', { replace: true });
    }

    const switchLanguage = (lang) => {
        i18n.changeLanguage(lang);
    };

    const currentLang = i18n.language;

    return (
        <div className="min-h-screen flex bg-surface-2 text-primary">
            {/* ── Chap panel: header butun kenglik bo'ylab, ostida [tashkilotlar ustuni | sidebar] ── */}
            <div className="hidden lg:flex flex-col bg-surface border-r border-border">
                <div className="p-6 border-b border-border flex items-center gap-3">
                    <div className="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center shadow-sm overflow-hidden">
                        <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-sm font-bold leading-tight">BI by CAU Admin</h1>
                        <CurrentOrgCaption />
                    </div>
                </div>

            <div className="flex-1 flex min-h-0">
            {/* Tashkilotlar ustuni (Slack uslubi) — faqat 2+ tashkilot bo'lsa; menyu bilan bir balandlikdan boshlanadi */}
            {showOrgRail && <OrganizationRail />}

            {/* ── Sidebar ── */}
            <aside className="w-64 flex flex-col">

                <nav className="flex-1 p-4 space-y-2">
                    <div className="px-4 mb-2 text-[10px] font-bold text-muted uppercase tracking-wider">{t('admin.management')}</div>
                    {mainNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors
                ${isActive ? 'bg-brand text-inverse shadow-brand-sm' : 'text-secondary hover:bg-surface-2 hover:text-primary'}
              `}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 space-y-2">
                    <div className="px-4 mb-2 text-[10px] font-bold text-muted uppercase tracking-wider">{t('admin.system')}</div>
                    {bottomNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors
                ${isActive ? 'bg-brand text-inverse shadow-brand-sm' : 'text-secondary hover:bg-surface-2 hover:text-primary'}
              `}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </NavLink>
                    ))}
                </div>

                <div className="px-6 py-4 border-t border-border flex items-center gap-2">
                    <div className="flex-1 flex rounded-lg overflow-hidden border border-border">
                        {[{ code: 'ru', label: 'Рус' }, { code: 'uz', label: 'O\'zbek' }, { code: 'en', label: 'Eng' }].map(({ code: lang, label }) => (
                            <button
                                key={lang}
                                onClick={() => switchLanguage(lang)}
                                className={`flex-1 py-1 text-[10px] font-bold transition-colors ${currentLang === lang ? 'bg-brand text-inverse' : 'text-muted hover:bg-surface-2'
                                    }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={toggleTheme}
                        className="p-2 rounded-lg border border-border text-muted hover:bg-surface-2 transition-colors flex-shrink-0"
                        title={isDark ? t('theme.light', 'Светлая тема') : t('theme.dark', 'Тёмная тема')}
                    >
                        {isDark ? <Icons.Sun className="w-4 h-4" /> : <Icons.Moon className="w-4 h-4" />}
                    </motion.button>
                </div>

                <div className="p-4 border-t border-border">
                    <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-surface-2 rounded-xl border border-border">
                        <div className="w-8 h-8 flex-shrink-0 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold overflow-hidden">
                            {user?.show_photo && user?.photo ? (
                                <img src={`${import.meta.env.VITE_API_URL || ''}${user.photo}`} alt={user.login} className="w-full h-full object-cover" />
                            ) : (
                                user?.last_name_ru?.charAt(0) || user?.last_name_en?.charAt(0) || 'A'
                            )}
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-sm font-bold truncate">
                                {(user?.last_name_ru || user?.last_name_uz || user?.last_name_en)
                                    ? (() => {
                                        const lang = i18n.language === 'en' ? 'en' : (i18n.language === 'uz' ? 'uz' : 'ru');
                                        const lastName = user[`last_name_${lang}`] || user.last_name_en || user.last_name_ru || user.last_name_uz || '';
                                        const firstName = user[`first_name_${lang}`] || user.first_name_en || user.first_name_ru || user.first_name_uz || '';
                                        const initial = getInitial(firstName);
                                        return `${lastName} ${initial}.`;
                                      })()
                                    : user?.login}
                            </p>
                            <p className="text-xs text-muted truncate">{t('admin.administrator')}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/panel" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-surface-2 border border-border text-xs font-semibold hover:text-brand transition-colors">
                            <ArrowLeft className="w-4 h-4" /> {t('admin.panel')}
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-error/10 text-error text-xs font-semibold hover:bg-error hover:text-inverse transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> {t('logout')}
                        </button>
                    </div>
                </div>
            </aside>
            </div>
            </div>

            {/* ── Main Content ── */}
            <main className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Mobile Header (optional, skipping for brevity but padding top is good) */}
                <div id="admin-content" className="flex-1 overflow-auto p-6 md:p-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
